import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import {
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Button,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AddIcon from "@mui/icons-material/Add";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useApi } from "../../utils/api";
import { useUserInfo } from "../../utils/user";
import AddWorkoutDetails from "../AddWorkoutDetails";
import ProgramCreatorAiDrawer from "./ProgramCreatorAiDrawer";
import Loading from "../Layout/Loading";
import { outlineToWorkouts } from "../../utils/outlineToWorkout";
import { outlineTextToLines } from "../../utils/outlineTextParser";
import { exerciseNameSetFromWorkouts } from "../../utils/exerciseNameMatch";
import WorkoutSummaryCard from "./WorkoutSummaryCard";
import {
  clientProgramStatusFromWorkouts,
  clientProgramSortKey,
  programUrgencyColor,
  programUrgencyLabel,
  workoutsInWeek,
} from "../../utils/clientProgramStatus";

const SIDEBAR_WIDTH = 320;
const MIDDLE_WIDTH = 300;

const sectionHeaderSx = {
  fontWeight: 700,
  fontSize: "1.05rem",
  lineHeight: 1.3,
};

const programHeaderSx = {
  fontWeight: 700,
  fontSize: "1.2rem",
  lineHeight: 1.25,
};

const ProgramCreator = () => {
  const { getClients, getWorkoutsForUser, addWorkoutForUser } = useApi();
  const { id: coachId, name: coachName } = useUserInfo();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width: 900px)");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(true);
  const [clientId, setClientId] = useState("");
  const [clientProgramStatuses, setClientProgramStatuses] = useState({});
  const [loadingProgramStatuses, setLoadingProgramStatuses] = useState(false);
  const [clientListSearch, setClientListSearch] = useState("");
  const [weekAnchor, setWeekAnchor] = useState(() =>
    moment().subtract(1, "week").startOf("week")
  );
  const [weekWorkouts, setWeekWorkouts] = useState([]);
  const [priorWeekWorkouts, setPriorWeekWorkouts] = useState([]);
  const [workoutsLoading, setWorkoutsLoading] = useState(false);
  const [draftExerciseNames, setDraftExerciseNames] = useState(() => new Set());

  const [programDays, setProgramDays] = useState([{ id: "day-0", draft: null }]);
  const [selectedDayIndex, setSelectedDayIndex] = useState(0);
  const [saveNonce, setSaveNonce] = useState(0);
  const [aiDrawerOpen, setAiDrawerOpen] = useState(false);

  const weekStartMs = weekAnchor.clone().startOf("week").valueOf();

  const weekLabel = useMemo(() => {
    const start = moment(weekStartMs).startOf("week");
    const end = moment(weekStartMs).endOf("week");
    return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
  }, [weekStartMs]);

  const nextWeekLabel = useMemo(() => {
    const start = moment().add(1, "week").startOf("week");
    const end = moment().add(1, "week").endOf("week");
    return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
  }, []);

  const handleDraftExerciseNamesChange = useCallback((set) => {
    setDraftExerciseNames(set instanceof Set ? set : new Set());
  }, []);

  const fetchClients = useCallback(async () => {
    setLoadingClients(true);
    try {
      const c = await getClients();
      const active = (c || []).filter((client) => !client.deleted);
      setClients(active);
    } catch (e) {
      console.error(e);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, [getClients]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  const coachClientsOnly = useMemo(() => {
    if (!coachId || !coachName) return clients;
    return clients.filter((c) => c.id !== coachId);
  }, [clients, coachId, coachName]);

  const clientsByProgramUrgency = useMemo(() => {
    return [...coachClientsOnly].sort((a, b) => {
      const sortA = clientProgramSortKey(clientProgramStatuses[a.id]);
      const sortB = clientProgramSortKey(clientProgramStatuses[b.id]);
      if (sortA !== sortB) return sortA - sortB;
      return a.name.localeCompare(b.name);
    });
  }, [coachClientsOnly, clientProgramStatuses]);

  const filteredClients = useMemo(() => {
    const q = clientListSearch.trim().toLowerCase();
    if (!q) return clientsByProgramUrgency;
    return clientsByProgramUrgency.filter(
      (client) =>
        client.name?.toLowerCase().includes(q) ||
        client.email?.toLowerCase().includes(q)
    );
  }, [clientsByProgramUrgency, clientListSearch]);

  useEffect(() => {
    const cid = searchParams.get("clientId");
    if (cid && coachClientsOnly.some((c) => c.id === cid)) {
      setClientId(cid);
    } else if (!cid && filteredClients.length > 0) {
      setClientId(filteredClients[0].id);
      setSearchParams({ clientId: filteredClients[0].id }, { replace: true });
    } else if (!cid) {
      setClientId("");
    }
  }, [searchParams, coachClientsOnly, filteredClients, setSearchParams]);

  const selectClient = useCallback(
    (id) => {
      if (id) {
        setSearchParams({ clientId: id }, { replace: false });
      } else {
        setSearchParams({}, { replace: false });
      }
      if (isMobile) setSidebarOpen(false);
    },
    [setSearchParams, isMobile]
  );

  const fetchClientProgramStatuses = useCallback(async () => {
    if (coachClientsOnly.length === 0) {
      setClientProgramStatuses({});
      return;
    }
    setLoadingProgramStatuses(true);
    const from = moment().subtract(12, "weeks").startOf("week").unix();
    const to = moment().add(6, "months").endOf("week").unix();
    try {
      const results = await Promise.all(
        coachClientsOnly.map(async (client) => {
          try {
            const data = await getWorkoutsForUser(client.id, from, to);
            const workouts = Array.isArray(data) ? data : [];
            const status = clientProgramStatusFromWorkouts(workouts);
            return [client.id, status];
          } catch (e) {
            console.error(e);
            return [client.id, null];
          }
        })
      );
      setClientProgramStatuses(Object.fromEntries(results));
    } finally {
      setLoadingProgramStatuses(false);
    }
  }, [coachClientsOnly, getWorkoutsForUser]);

  useEffect(() => {
    fetchClientProgramStatuses();
  }, [fetchClientProgramStatuses]);

  const prevClientIdRef = useRef(undefined);
  useEffect(() => {
    if (prevClientIdRef.current !== undefined && prevClientIdRef.current !== clientId) {
      setProgramDays([{ id: "day-0", draft: null }]);
      setSelectedDayIndex(0);
      setDraftExerciseNames(new Set());
      setWeekAnchor(moment().subtract(1, "week").startOf("week"));
    }
    prevClientIdRef.current = clientId;
  }, [clientId]);

  const fetchWeekWorkouts = useCallback(async () => {
    if (!clientId) {
      setWeekWorkouts([]);
      setPriorWeekWorkouts([]);
      return;
    }
    setWorkoutsLoading(true);
    try {
      const weekMoment = moment(weekStartMs);
      const from = weekMoment.clone().subtract(1, "week").startOf("week").unix();
      const to = weekMoment.clone().endOf("week").unix();
      const data = await getWorkoutsForUser(clientId, from, to);
      const list = Array.isArray(data) ? data : [];
      const viewed = workoutsInWeek(list, weekMoment).sort(
        (a, b) => (a.date || 0) - (b.date || 0)
      );
      const prior = workoutsInWeek(
        list,
        weekMoment.clone().subtract(1, "week")
      );
      setWeekWorkouts(viewed);
      setPriorWeekWorkouts(prior);
    } catch (e) {
      console.error(e);
      setWeekWorkouts([]);
      setPriorWeekWorkouts([]);
    } finally {
      setWorkoutsLoading(false);
    }
  }, [clientId, weekStartMs, getWorkoutsForUser]);

  useEffect(() => {
    fetchWeekWorkouts();
  }, [fetchWeekWorkouts]);

  const priorWeekExerciseNameSet = useMemo(
    () => exerciseNameSetFromWorkouts(priorWeekWorkouts),
    [priorWeekWorkouts]
  );

  const activeClient = useMemo(
    () => coachClientsOnly.find((c) => c.id === clientId) || null,
    [coachClientsOnly, clientId]
  );

  const buildDraftWorkout = useCallback((draft, formIndex) => {
    const base = {
      name: "",
      description: "",
      exercises: [],
      date: moment().add(1, "week").startOf("week").add(formIndex, "days"),
    };
    if (!draft) return base;
    return {
      ...base,
      name: draft.name || base.name,
      description: draft.description || "",
      exercises: draft.exercises || [],
    };
  }, []);

  const activeDayIndex = useMemo(() => {
    if (programDays.length === 0) return 0;
    return Math.min(selectedDayIndex, programDays.length - 1);
  }, [selectedDayIndex, programDays.length]);

  const activeDay = programDays[activeDayIndex] ?? programDays[0];

  const editToolbarTitle = useMemo(() => {
    const name = activeDay?.draft?.name?.trim();
    return `Edit - ${name || "Untitled workout"}`;
  }, [activeDay?.draft?.name]);

  const handleDraftWorkoutChange = useCallback(
    (snapshot) => {
      setProgramDays((prev) =>
        prev.map((day, i) =>
          i === activeDayIndex ? { ...day, draft: snapshot } : day
        )
      );
    },
    [activeDayIndex]
  );

  const handleAddDay = () => {
    setProgramDays((prev) => {
      const next = [...prev, { id: `day-${Date.now()}`, draft: null }];
      setSelectedDayIndex(next.length - 1);
      return next;
    });
  };

  const handleApplyAiProgram = useCallback((outlineText) => {
    const lines = outlineTextToLines(outlineText);
    const workouts = outlineToWorkouts(lines);
    setProgramDays(
      workouts.map((w, i) => ({
        id: `ai-day-${Date.now()}-${i}`,
        draft: {
          name: w.name || "",
          description: w.description || "",
          exercises: w.exercises || [],
        },
      }))
    );
    setSelectedDayIndex(0);
    setSaveNonce((n) => n + 1);
    setAiDrawerOpen(false);
  }, []);

  const handleSaveWorkout = async (workoutData, formIndex) => {
    await addWorkoutForUser(clientId, workoutData);
    await fetchWeekWorkouts();
    await fetchClientProgramStatuses();
    setSaveNonce((n) => n + 1);
    setProgramDays((prev) => {
      if (prev.length > 1) {
        const next = prev.filter((_, i) => i !== formIndex);
        setSelectedDayIndex((idx) => Math.min(idx, Math.max(0, next.length - 1)));
        return next;
      }
      setSelectedDayIndex(0);
      return [{ id: `day-${Date.now()}`, draft: null }];
    });
  };

  if (loadingClients) {
    return (
      <Box sx={{ mt: 10, display: "flex", justifyContent: "center" }}>
        <Loading />
      </Box>
    );
  }

  const noClients = coachClientsOnly.length === 0;

  const renderClientList = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ ...sectionHeaderSx, mb: 0.5 }}>
          Clients
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          Sorted by who needs a new program soonest.
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search clients..."
          value={clientListSearch}
          onChange={(e) => setClientListSearch(e.target.value)}
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-track": { background: "#f1f1f1" },
          "&::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "4px",
          },
        }}
      >
        {loadingProgramStatuses ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : filteredClients.length === 0 ? (
          <Paper sx={{ m: 2, p: 2 }}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {clientListSearch.trim()
                ? `No clients matching "${clientListSearch.trim()}"`
                : "No clients to show."}
            </Typography>
          </Paper>
        ) : (
          filteredClients.map((client) => {
            const status = clientProgramStatuses[client.id];
            const weeksRemaining = status?.weeksRemaining;
            const isActive = clientId === client.id;
            const showNextWeekFallback =
              status?.hasNextWeekProgram &&
              weeksRemaining === 0 &&
              !status?.statusFromNextWeek;
            const nameColor = isActive
              ? "white"
              : programUrgencyColor(weeksRemaining);

            return (
              <Paper
                key={client.id}
                onClick={() => selectClient(client.id)}
                sx={{
                  mx: 2,
                  mb: 2,
                  p: 2,
                  backgroundColor: isActive ? theme.palette.primary.main : "white",
                  color: isActive ? "white" : "inherit",
                  cursor: "pointer",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.92rem",
                    lineHeight: 1.35,
                    fontWeight: 600,
                    color: nameColor,
                  }}
                >
                  {client.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontSize: "0.82rem",
                    color: isActive ? "rgba(255,255,255,0.85)" : "text.secondary",
                  }}
                >
                  {client.email}
                </Typography>
                {status ? (
                  <Typography
                    variant="caption"
                    component="div"
                    sx={{
                      mt: 1,
                      fontSize: "0.72rem",
                      lineHeight: 1.35,
                      color: isActive ? "rgba(255,255,255,0.9)" : "text.secondary",
                    }}
                  >
                    {status.currentWeek != null && status.programLength != null
                      ? `Week ${status.currentWeek} of ${status.programLength} · `
                      : ""}
                    {programUrgencyLabel(weeksRemaining, showNextWeekFallback)}
                    {showNextWeekFallback && (
                      <>
                        <br />
                        Next week programmed
                      </>
                    )}
                  </Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 1,
                      display: "block",
                      color: isActive ? "rgba(255,255,255,0.85)" : "text.secondary",
                    }}
                  >
                    {programUrgencyLabel(null)}
                  </Typography>
                )}
              </Paper>
            );
          })
        )}
      </Box>
    </Box>
  );

  if (noClients) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Program creator
        </Typography>
        <Paper sx={{ p: 3 }}>
          <Typography>
            Add a client from <strong>Clients</strong> to use the program
            creator.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: 0,
        height: "calc(100vh - 72px)",
        mt: 1,
      }}
    >
      {/* Left: client list */}
      <Box
        sx={{
          flexShrink: 0,
          width: isMobile ? 0 : SIDEBAR_WIDTH,
          overflow: "hidden",
          display: isMobile ? (sidebarOpen ? "flex" : "none") : "flex",
          flexDirection: "column",
          minHeight: 0,
          borderRight: isMobile ? 0 : 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
          ...(isMobile && {
            position: "fixed",
            top: 72,
            left: 0,
            bottom: 0,
            width: SIDEBAR_WIDTH,
            zIndex: 1200,
            boxShadow: 3,
          }),
        }}
      >
        {renderClientList()}
      </Box>

      {/* Middle: previous week workouts */}
      <Box
        sx={{
          flexShrink: 0,
          width: isMobile ? "100%" : MIDDLE_WIDTH,
          display: isMobile && sidebarOpen ? "none" : "flex",
          flexDirection: "column",
          minHeight: 0,
          borderRight: isMobile ? 0 : 1,
          borderColor: "divider",
          backgroundColor: "background.paper",
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
          {isMobile && (
            <IconButton
              onClick={() => setSidebarOpen(true)}
              size="small"
              sx={{ mb: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="subtitle1" sx={{ ...sectionHeaderSx, mb: 1 }}>
            Workouts{activeClient ? ` - ${activeClient.name}` : ""}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
            }}
          >
            <IconButton
              size="small"
              onClick={() =>
                setWeekAnchor((w) => w.clone().subtract(1, "week"))
              }
              aria-label="Previous week"
              disabled={!clientId}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1, textAlign: "center", minWidth: 0 }}
            >
              {weekLabel}
            </Typography>
            <IconButton
              size="small"
              onClick={() => setWeekAnchor((w) => w.clone().add(1, "week"))}
              aria-label="Next week"
              disabled={!clientId}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {!clientId ? (
            <Typography variant="body2" color="text.secondary">
              Select a client to view workouts.
            </Typography>
          ) : workoutsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : weekWorkouts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No workouts this week.
            </Typography>
          ) : (
            weekWorkouts.map((w) => (
              <WorkoutSummaryCard
                key={w.id}
                workout={w}
                draftExerciseNames={draftExerciseNames}
                subtitle={moment.unix(w.date).format("dddd, MMM D")}
                theme={theme}
              />
            ))
          )}
        </Box>
      </Box>

      {/* Right: new program builder */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: isMobile && sidebarOpen ? "none" : "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            borderBottom: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              mb: 1,
            }}
          >
            <Box>
              <Typography variant="h6" sx={programHeaderSx}>
                New program — {nextWeekLabel}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => setAiDrawerOpen(true)}
                disabled={!clientId}
              >
                AI assistant
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={handleAddDay}
                disabled={!clientId}
              >
                Add a day
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: 1.5,
              overflowX: "auto",
              pb: 1,
              "&::-webkit-scrollbar": { height: "6px" },
            }}
          >
            {programDays.map((day, index) => {
              const draftWorkout = buildDraftWorkout(day.draft, index);
              const isSelected = index === activeDayIndex;
              return (
                <Box key={day.id} sx={{ minWidth: 200, maxWidth: 240, flexShrink: 0 }}>
                  <WorkoutSummaryCard
                    workout={draftWorkout}
                    draftExerciseNames={draftExerciseNames}
                    selected={isSelected}
                    onClick={() => setSelectedDayIndex(index)}
                    subtitle={draftWorkout.date.format("dddd, MMM D")}
                    theme={theme}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          {clientId && activeDay ? (
            <AddWorkoutDetails
              key={`${clientId}-${saveNonce}-${activeDay.id}-${activeDayIndex}-${buildDraftWorkout(activeDay.draft, activeDayIndex).date.valueOf()}`}
              open
              handleClose={() => {}}
              userId={clientId}
              workout={buildDraftWorkout(activeDay.draft, activeDayIndex)}
              onSave={(data) => handleSaveWorkout(data, activeDayIndex)}
              onWorkoutCopied={fetchWeekWorkouts}
              onWorkoutDeleted={fetchWeekWorkouts}
              onWorkoutUpdated={fetchWeekWorkouts}
              inline
              submitButtonId={
                programDays.length > 1
                  ? `program-creator-save-workout-${activeDayIndex}`
                  : "program-creator-save-workout"
              }
              previousWeekExerciseNameSet={priorWeekExerciseNameSet}
              onDraftExerciseNamesChange={handleDraftExerciseNamesChange}
              onDraftWorkoutChange={handleDraftWorkoutChange}
              showPurchasedProducts={false}
              toolbarTitle={editToolbarTitle}
              toolbarActionsRight
            />
          ) : (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">
                Select a client to start building their program.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <ProgramCreatorAiDrawer
        open={aiDrawerOpen}
        onClose={() => setAiDrawerOpen(false)}
        clientId={clientId}
        clientName={activeClient?.name}
        weekLabel={nextWeekLabel}
        referenceWorkouts={weekWorkouts}
        referenceWeekLabel={weekLabel}
        onApplyProgram={handleApplyAiProgram}
      />
    </Box>
  );
};

export default ProgramCreator;
